import { AzureDataBricksLinkedService } from './../../linkedservice/azure-data-bricks.linkedservice';
import { NotebookActivity } from './../../pipeline/notebook.activity';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ReferenceAdapter } from '../reference.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config-string.util';
import { ValueAdapter } from '../value.adapter';
import _ from 'lodash';
import { databricksLibraryWithExpressionToResource, databricksLibraryWithExpressionToModel, databricksLibraryToModel } from 'app/model/pipeline/databricks-library.util';
import { convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import { databricksInitScriptsToArrayModel, databricksInitScriptsToResource, databricksParameterToModel, databricksParameterToResource } from 'app/model/pipeline/databricks-parameter.util';
import { ParameterConfigString } from 'app/model/pipeline/parameter-config-string';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { ActivityConnectionContext, IDatabricksNotebookActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { ModelService } from 'app/model/services/model.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { IFrameService } from 'app/services/iframe.service';
import { ModelConstants } from 'shell-models-lib';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(NotebookActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            notebookPath: '*',
            baseParameters: '*',
            libraries: '*',
            mainClassName: '*',
            pythonFile: '*',
            parameters: '*',
            jobId: '*',
            jobParameters: '*',
            existingClusterId: '*',
            instancePoolId: '*',
            newClusterVersion: '*',
            newClusterNumOfWorker: '*',
            newClusterNodeType: '*',
            newClusterSparkConf: '*',
            newClusterSparkEnvVars: '*',
            newClusterCustomTags: '*',
            newClusterLogDestinationSettings: '*',
            newClusterLogDestination: '*',
            newClusterInitScripts: '*',
            newClusterDriverNodeType: '*',
            clusterOption: '*',
            dataSecurityMode: '*'
        },
        externalReferences: '*'
    }
})
export class NotebookActivityAdapter extends ActivityAsyncAdapter<NotebookActivity, Model.DatabricksNotebookActivity, IDatabricksNotebookActivityContext> {

    public model(): IModel {
        return Metamodel.modelFor(NotebookActivity);
    }

    public override async toFabricCopilotContext(activity: NotebookActivity): Promise<IDatabricksNotebookActivityContext> {
        if (activity) {
            const activityContext: IDatabricksNotebookActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    AzureDataBricksLinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IDatabricksNotebookActivityContext,
        model?: NotebookActivity
    ): Promise<NotebookActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(this.context.contextData, 'IFrameService');
                model.linkedService = (await findLinkedServiceFromContext<AzureDataBricksLinkedService>(context.Connection, { modelService, iframeService })).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              AzureDataBricksLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    if (lsAdapter) {
                        await lsAdapter.fromFabricCopilotContext(
                            context.Connection,
                            model.linkedService
                        );
                    }
                }
            }
        }
        return model;
    }

    protected async _toResource(activity: NotebookActivity): Promise<Model.DatabricksNotebookActivity> {
        const resource: Model.DatabricksNotebookActivity = super._getCommonActivityProperties(activity, Metamodel.modelFor(NotebookActivity).name) as Model.DatabricksNotebookActivity;

        if (activity.type === 'Notebook') {
            resource.typeProperties = {
                notebookPath: ValueAdapter.toResource(activity, NotebookActivity.NOTEBOOK_PATH),
                baseParameters: toParameterObject(activity.parameters as ParameterConfigString[]),
                libraries: databricksLibraryWithExpressionToResource(activity.libraries, NotebookActivity.LIBRARIES.templateValue(activity))
            };
        } else if (activity.type === 'Jar') {
            resource.typeProperties = {
                mainClassName: ValueAdapter.toResource(activity, NotebookActivity.MAIN_CLASS_NAME),
                parameters: databricksParameterToResource(activity.parameters),
                libraries: databricksLibraryWithExpressionToResource(activity.libraries, NotebookActivity.LIBRARIES.templateValue(activity))
            };
        } else if (activity.type === 'Python') {
            resource.typeProperties = {
                pythonFile: ValueAdapter.toResource(activity, NotebookActivity.PYTHON_FILE),
                parameters: databricksParameterToResource(activity.parameters),
                libraries: databricksLibraryWithExpressionToResource(activity.libraries, NotebookActivity.LIBRARIES.templateValue(activity))
            };
        } else {
            resource.typeProperties = {
                jobId: ValueAdapter.toResource(activity, NotebookActivity.JOBID),
                baseParameters: toParameterObject(activity.parameters as ParameterConfigString[]),
                jobParameters:  toParameterObject(activity.jobParameters as ParameterConfigString[]),
                libraries: databricksLibraryWithExpressionToResource(activity.libraries, NotebookActivity.LIBRARIES.templateValue(activity))
            };
        }

        if (__IS_FABRIC_DPE__ || this.context.adapterConfig?.convertToFabric) {
            if (activity.useClusterMode === 'new') {
                resource.typeProperties.newClusterNodeType = ValueAdapter.toResource(activity, NotebookActivity.NODE_TYPE);
                resource.typeProperties.newClusterNumOfWorker = ValueAdapter.toResource(activity, NotebookActivity.NUM_WORKER);
                resource.typeProperties.newClusterSparkConf = toParameterObject(activity.newClusterSparkConf);
                resource.typeProperties.newClusterSparkEnvVars = toParameterObject(activity.newClusterSparkEnvVars);
                resource.typeProperties.newClusterCustomTags = toParameterObject(activity.newClusterCustomTags);
                resource.typeProperties.newClusterVersion = ValueAdapter.toResource(activity, NotebookActivity.VERSION);
                if (GlobalVariables.enableDatabricksClusterLogVolumes) {
                    const newClusterLogDestination = ValueAdapter.toResource(activity, NotebookActivity.DBFS_DESTINATION) as string;
                    const destinationType = this.toClusterLogDestinationTypeResource(
                        ValueAdapter.toResource(activity, NotebookActivity.DESTINATION_TYPE) as string
                    );
                    if (!stringIsNullOrEmpty(newClusterLogDestination) && !stringIsNullOrEmpty(destinationType)) {
                        resource.typeProperties.newClusterLogDestinationSettings = {
                            type: destinationType,
                            path: newClusterLogDestination
                        };
                    }
                } else {
                    resource.typeProperties.newClusterLogDestination = ValueAdapter.toResource(activity, NotebookActivity.DBFS_DESTINATION);
                }
                resource.typeProperties.newClusterDriverNodeType = ValueAdapter.toResource(activity, NotebookActivity.DRIVER_NODE_TYPE);
                resource.typeProperties.clusterOption = ValueAdapter.toResource(activity, NotebookActivity.CLUSTER_OPTION);
                resource.typeProperties.newClusterInitScripts = databricksInitScriptsToResource(activity.newClusterInitScripts);
                resource.typeProperties.dataSecurityMode = ValueAdapter.toResource(activity, NotebookActivity.DATA_SECURITY_MODE);
                if (GlobalVariables.enableNewClusterAttributes) {
                    const policyId = ValueAdapter.toResource(activity, NotebookActivity.POLICY_ID);
                    if (!stringIsNullOrEmpty(policyId)) {
                        resource.typeProperties.policyId = policyId;
                    }

                    if (activity.newClusterAttributes.availability === 'SpotWithFallback') {
                        resource.typeProperties.newClusterAttributes = ValueAdapter.toResource(activity, NotebookActivity.ADDITIONAL_CLUSTER_ATTRIBUTES);
                    }

                }
            } else if (activity.useClusterMode === 'exist') {
                resource.typeProperties.existingClusterId = ValueAdapter.toResource(activity, NotebookActivity.EXISTING_CLUSTER_ID);
            } else if (activity.useClusterMode === 'pool') {
                resource.typeProperties.instancePoolId = ValueAdapter.toResource(activity, NotebookActivity.INSTANCE_POOL_ID);
                resource.typeProperties.newClusterNumOfWorker = ValueAdapter.toResource(activity, NotebookActivity.NUM_WORKER);
                resource.typeProperties.newClusterSparkConf = toParameterObject(activity.newClusterSparkConf);
                resource.typeProperties.newClusterSparkEnvVars = toParameterObject(activity.newClusterSparkEnvVars);
                resource.typeProperties.newClusterCustomTags = toParameterObject(activity.newClusterCustomTags);
                resource.typeProperties.newClusterVersion = ValueAdapter.toResource(activity, NotebookActivity.VERSION);
                if (GlobalVariables.enableDatabricksClusterLogVolumes) {
                    const newClusterLogDestination = ValueAdapter.toResource(activity, NotebookActivity.DBFS_DESTINATION) as string;
                    const destinationType = this.toClusterLogDestinationTypeResource(
                        ValueAdapter.toResource(activity, NotebookActivity.DESTINATION_TYPE) as string
                    );
                    if (!stringIsNullOrEmpty(newClusterLogDestination) && !stringIsNullOrEmpty(destinationType)) {
                        resource.typeProperties.newClusterLogDestinationSettings = {
                            type: destinationType,
                            path: newClusterLogDestination
                        };
                    }
                } else {
                    resource.typeProperties.newClusterLogDestination = ValueAdapter.toResource(activity, NotebookActivity.DBFS_DESTINATION);
                }
                resource.typeProperties.clusterOption = ValueAdapter.toResource(activity, NotebookActivity.CLUSTER_OPTION);
                resource.typeProperties.newClusterInitScripts = databricksInitScriptsToResource(activity.newClusterInitScripts);
                if (GlobalVariables.enableNewClusterAttributes) {
                    const policyId = ValueAdapter.toResource(activity, NotebookActivity.POLICY_ID);
                    if (!stringIsNullOrEmpty(policyId)) {
                        resource.typeProperties.policyId = policyId;
                    }

                    if (activity.newClusterAttributes.availability === 'SpotWithFallback') {
                        resource.typeProperties.newClusterAttributes = ValueAdapter.toResource(activity, NotebookActivity.ADDITIONAL_CLUSTER_ATTRIBUTES);
                    }

                }
            }
        }

        if (activity.linkedService) {
            resource.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService, activity.linkedServiceParameters);
        }
        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(resource, parsed);
        }

        return resource;
    }

    protected async _toModel(modelActivity: Model.DatabricksNotebookActivity): Promise<NotebookActivity> {
        const activity: NotebookActivity = new NotebookActivity(modelActivity.name);
        const resource = modelActivity as Model.DatabricksNotebookActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (resource.typeProperties) {
            if (resource.typeProperties.mainClassName) {
                ValueAdapter.toModel(activity, NotebookActivity.MAIN_CLASS_NAME, resource.typeProperties.mainClassName);
                ValueAdapter.toModel(activity, NotebookActivity.TYPE, 'Jar');
                activity.parameters = databricksParameterToModel(modelActivity.typeProperties.parameters, activity);
            } else if (resource.typeProperties.pythonFile) {
                ValueAdapter.toModel(activity, NotebookActivity.PYTHON_FILE, resource.typeProperties.pythonFile);
                ValueAdapter.toModel(activity, NotebookActivity.TYPE, 'Python');
                activity.parameters = databricksParameterToModel(modelActivity.typeProperties.parameters, activity);
            } else if (resource.typeProperties.notebookPath) {
                ValueAdapter.toModel(activity, NotebookActivity.NOTEBOOK_PATH, resource.typeProperties.notebookPath);
                activity.parameters = toParameterClass(modelActivity.typeProperties.baseParameters, activity);
            } else if (resource.typeProperties.jobId) {
                ValueAdapter.toModel(activity, NotebookActivity.JOBID, resource.typeProperties.jobId);
                ValueAdapter.toModel(activity, NotebookActivity.TYPE, 'Job');
                activity.parameters = toParameterClass(modelActivity.typeProperties.baseParameters, activity);
                activity.jobParameters = toParameterClass(modelActivity.typeProperties.jobParameters, activity);
            }
        }

        if (__IS_FABRIC_DPE__) {
            if (resource.typeProperties.existingClusterId) {
                activity.useClusterMode = 'exist';
                ValueAdapter.toModel(activity, NotebookActivity.EXISTING_CLUSTER_ID, resource.typeProperties.existingClusterId);
            } else if (resource.typeProperties.instancePoolId) {
                activity.useClusterMode = 'pool';
                ValueAdapter.toModel(activity, NotebookActivity.INSTANCE_POOL_ID, resource.typeProperties.instancePoolId);
                ValueAdapter.toModel(activity, NotebookActivity.NODE_TYPE, resource.typeProperties.newClusterNodeType);
                ValueAdapter.toModel(activity, NotebookActivity.NUM_WORKER, resource.typeProperties.newClusterNumOfWorker);
                activity.newClusterSparkConf = toParameterClass(resource.typeProperties.newClusterSparkConf, activity);
                activity.newClusterSparkEnvVars = toParameterClass(resource.typeProperties.newClusterSparkEnvVars, activity);
                activity.newClusterCustomTags = toParameterClass(resource.typeProperties.newClusterCustomTags, activity);
                ValueAdapter.toModel(activity, NotebookActivity.VERSION, resource.typeProperties.newClusterVersion);
                ValueAdapter.toModel(
                    activity,
                    NotebookActivity.DESTINATION_TYPE,
                    this.toClusterLogDestinationTypeModel(resource.typeProperties.newClusterLogDestinationSettings?.type)
                );
                ValueAdapter.toModel(
                    activity,
                    NotebookActivity.DBFS_DESTINATION,
                    resource.typeProperties.newClusterLogDestinationSettings?.path ?? resource.typeProperties.newClusterLogDestination
                );
                activity.newClusterInitScripts = databricksInitScriptsToArrayModel(resource.typeProperties.newClusterInitScripts, activity);
                ValueAdapter.toModel(activity, NotebookActivity.CLUSTER_OPTION, resource.typeProperties.clusterOption);
                if (GlobalVariables.enableNewClusterAttributes) {
                    ValueAdapter.toModel(activity, NotebookActivity.ADDITIONAL_CLUSTER_ATTRIBUTES, resource.typeProperties.newClusterAttributes);
                    ValueAdapter.toModel(activity, NotebookActivity.POLICY_ID, resource.typeProperties.policyId);
                }
            } else {
                activity.useClusterMode = 'new';
                ValueAdapter.toModel(activity, NotebookActivity.NODE_TYPE, resource.typeProperties.newClusterNodeType);
                ValueAdapter.toModel(activity, NotebookActivity.NUM_WORKER, resource.typeProperties.newClusterNumOfWorker);
                activity.newClusterSparkConf = toParameterClass(resource.typeProperties.newClusterSparkConf, activity);
                activity.newClusterSparkEnvVars = toParameterClass(resource.typeProperties.newClusterSparkEnvVars, activity);
                activity.newClusterCustomTags = toParameterClass(resource.typeProperties.newClusterCustomTags, activity);
                ValueAdapter.toModel(activity, NotebookActivity.VERSION, resource.typeProperties.newClusterVersion);
                ValueAdapter.toModel(
                    activity,
                    NotebookActivity.DESTINATION_TYPE,
                    this.toClusterLogDestinationTypeModel(resource.typeProperties.newClusterLogDestinationSettings?.type)
                );
                ValueAdapter.toModel(
                    activity,
                    NotebookActivity.DBFS_DESTINATION,
                    resource.typeProperties.newClusterLogDestinationSettings?.path ?? resource.typeProperties.newClusterLogDestination
                );
                activity.newClusterInitScripts = databricksInitScriptsToArrayModel(resource.typeProperties.newClusterInitScripts, activity);
                ValueAdapter.toModel(activity, NotebookActivity.DRIVER_NODE_TYPE, resource.typeProperties.newClusterDriverNodeType);
                ValueAdapter.toModel(activity, NotebookActivity.CLUSTER_OPTION, resource.typeProperties.clusterOption);
                ValueAdapter.toModel(activity, NotebookActivity.DATA_SECURITY_MODE, resource.typeProperties.dataSecurityMode);
                if (GlobalVariables.enableNewClusterAttributes) {
                    ValueAdapter.toModel(activity, NotebookActivity.ADDITIONAL_CLUSTER_ATTRIBUTES, resource.typeProperties.newClusterAttributes);
                    ValueAdapter.toModel(activity, NotebookActivity.POLICY_ID, resource.typeProperties.policyId);
                }
            }
        }

        if (resource.linkedServiceName != null) {
            const ls = getFabricLsForParameterizedConnections(
                resource.linkedServiceName,
                ModelConstants.azureDatabricksLinkedService,
                activity
            );
            activity.linkedService = ls ? (ls as AzureDataBricksLinkedService) : ReferenceAdapter.toProxyModel<AzureDataBricksLinkedService>(Metamodel.modelFor(AzureDataBricksLinkedService),
                resource.linkedServiceName, this.context);
            if (resource.linkedServiceName.parameters) {
                // Parameter value for linked service
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.linkedService,
                    activity.linkedServiceParameters,
                    resource.linkedServiceName.parameters);
            }
        }

        if (modelActivity.typeProperties && modelActivity.typeProperties.libraries) {
            databricksLibraryWithExpressionToModel(modelActivity.typeProperties.libraries, NotebookActivity.LIBRARIES, activity, () => {
                activity.libraries = databricksLibraryToModel(modelActivity.typeProperties.libraries);
            });
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, resource, NotebookActivity.MODEL));

        return activity;
    }

    private toClusterLogDestinationTypeResource(value: string | undefined): Model.IDatabricksClusterLogDestinationType | undefined {
        if (stringIsNullOrEmpty(value)) {
            return undefined;
        }

        return value.toLowerCase();
    }

    private toClusterLogDestinationTypeModel(value: Model.IDatabricksClusterLogDestinationType | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        switch (value.toLowerCase()) {
            case 'dbfs':
                return 'Dbfs';
            case 'volumes':
                return 'Volumes';
            case 'abfs':
                return 'Abfs';
            case 'abfss':
                return 'Abfss';
            default:
                return value;
        }
    }
}
